/* eslint-disable unicorn/prevent-abbreviations */
import { RequirementWorkflowType } from "@clipboard-health/contract-worker-app-bff";
import {
  PageHeaderWrapper,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { DocumentStatus } from "@src/appV2/Accounts/Documents/types";
import { FullScreenDialog } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { DocumentSubmissionErrorBottomSheet } from "@src/appV2/redesign/Documents/Submission/ErrorBottomSheet";
import { SHIFT_DISCOVERY_SHIFT_DOCUMENT_SUBMITTED_MODAL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { type HelloSignModalRoutePathParams } from "@src/appV2/redesign/ShiftDiscovery/types";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import { useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import { useDefinedWorker } from "../../../../../Worker/useDefinedWorker";
import { PageHeaderTopRow } from "../../../../components/PageHeaderTopRow";
import { ESignWorkflowSessionStatus } from "../../../../Documents/Details/Workflows/PandaDoc/constants";
import { PandaDocEmbeddedSessionLoadingBottomSheet } from "../../../../Documents/Details/Workflows/PandaDoc/EmbeddedSessionLoadingBottomSheet";
import { getDocumentStatusOfESignWorkflowSession } from "../../../../Documents/Details/Workflows/PandaDoc/getDocumentStatusOfESignWorkflowSession";
import { PandaDocEmbeddedSigning } from "../../../../Documents/Details/Workflows/PandaDoc/PandaDocEmbeddedSigning";
import { useSignableESignWorkflowSession } from "../../../../Documents/Details/Workflows/PandaDoc/useSignableESignWorkflowSession";
import { useUpdateESignWorkflowSession } from "../../../../Documents/Details/Workflows/PandaDoc/useUpdateESignWorkflowSession";
import { DocumentSubmissionInProgressBottomSheet } from "../../../../Documents/Submission/InProgressBottomSheet";

interface InstantReviewPandaDocFormBottomSheetProps {
  modalState: UseModalState;
}

export function InstantReviewPandaDocFormBottomSheet(
  props: InstantReviewPandaDocFormBottomSheetProps
) {
  const { modalState } = props;
  const loadingPandaDocModalState = useModalState();
  const { openModal: openLoadingModal } = loadingPandaDocModalState;
  const worker = useDefinedWorker();
  const workerId = worker.userId;
  const submittingDocumentModalState = useModalState();
  const errorModalState = useModalState();
  const { navigateToModal } = useShiftModalsDataContext();
  const pandaDocSignCtaClickedAtRef = useRef<number>();
  const { shiftId, requirementId } = useParams<HelloSignModalRoutePathParams>();

  const { mutate: loadSignableESignWorkflowSession, data: signableESignWorkflowSession } =
    useSignableESignWorkflowSession({
      requirementId,
      onError: useCallback(
        (error: unknown) => {
          logEvent(APP_V2_USER_EVENTS.SHIFT_BOOKING_FLOW_DOCUMENT_SUBMISSION_FAILED, {
            shiftId: shiftId ?? "",
            requirementId,
            requirementWorkflowType: RequirementWorkflowType.SIGN_WITH_HELLOSIGN,
          });
          logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
            error,
            metadata: { requirementId, shiftId },
          });
          errorModalState.openModal();
        },
        [errorModalState, shiftId, requirementId]
      ),
    });
  const { mutateAsync: updateESignWorkflowSession } = useUpdateESignWorkflowSession();

  const onPandaDocCompleted = useCallback(async () => {
    submittingDocumentModalState.openModal();
    let documentStatus: DocumentStatus | undefined;
    try {
      if (!isDefined(signableESignWorkflowSession)) {
        throw new Error("signableESignWorkflowSession is not defined");
      }

      const updatedSessionResponse = await updateESignWorkflowSession({
        requirementId,
        sessionId: signableESignWorkflowSession.id,
        attributesToUpdate: { status: ESignWorkflowSessionStatus.SIGNED },
      });

      documentStatus = updatedSessionResponse
        ? await getDocumentStatusOfESignWorkflowSession({
            data: updatedSessionResponse.data,
            included: updatedSessionResponse.included,
          })
        : await getDocumentStatusOfESignWorkflowSession({
            requirementId,
            workerId,
          });
    } catch (error: unknown) {
      submittingDocumentModalState.closeModal();
      errorModalState.openModal();
      logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
        error,
        metadata: { requirementId, shiftId },
      });
      logEvent(APP_V2_USER_EVENTS.SHIFT_BOOKING_FLOW_DOCUMENT_SUBMISSION_FAILED, {
        shiftId: shiftId ?? "",
        requirementId,
        requirementWorkflowType: RequirementWorkflowType.SIGN_WITH_HELLOSIGN,
      });
      return;
    }

    logEvent(APP_V2_USER_EVENTS.PANDADOC_SDK_DOCUMENT_SIGNED, {
      requirementId,
      signType: "shiftBookingFlow",
    });

    logEvent(APP_V2_USER_EVENTS.SHIFT_BOOKING_FLOW_DOCUMENT_SUBMISSION_SUCCESSFUL, {
      shiftId: shiftId ?? "",
      requirementId,
      documentStatus: documentStatus ?? DocumentStatus.PENDING,
      requirementWorkflowType: RequirementWorkflowType.SIGN_WITH_HELLOSIGN,
    });
    submittingDocumentModalState.closeModal();
    navigateToModal(SHIFT_DISCOVERY_SHIFT_DOCUMENT_SUBMITTED_MODAL_PATH, {
      pathParams: {
        shiftId,
        requirementId,
        documentStatus: documentStatus ?? DocumentStatus.PENDING,
      },
      forceReplacePath: true,
    });
  }, [
    workerId,
    signableESignWorkflowSession,
    updateESignWorkflowSession,
    navigateToModal,
    submittingDocumentModalState,
    errorModalState,
    shiftId,
    requirementId,
  ]);

  useEffect(() => {
    if (requirementId) {
      openLoadingModal();
      pandaDocSignCtaClickedAtRef.current = Date.now();
      loadSignableESignWorkflowSession({});
      logEvent(APP_V2_USER_EVENTS.PANDADOC_SIGN_CTA_CLICKED, {
        requirementId,
        signType: "shiftBookingFlow",
      });
    }
  }, [requirementId, openLoadingModal, loadSignableESignWorkflowSession]);

  return (
    <>
      <FullScreenDialog skipTopSafeAreaPadding modalState={modalState}>
        <PageHeaderWrapper variant="tertiary">
          <PageHeaderTopRow
            title="Sign Document"
            onClickBack={() => {
              modalState.closeModal();
            }}
          />
        </PageHeaderWrapper>
        <Box sx={{ height: "100%", overflow: "auto" }}>
          {isDefined(signableESignWorkflowSession?.attributes.esignProviderData?.sessionId) && (
            <PandaDocEmbeddedSigning
              pandaDocSessionId={
                signableESignWorkflowSession?.attributes.esignProviderData.sessionId
              }
              onDocumentLoaded={() => {
                loadingPandaDocModalState.closeModal();
                const timeToLoadInMilliseconds = pandaDocSignCtaClickedAtRef.current
                  ? Date.now() - pandaDocSignCtaClickedAtRef.current
                  : undefined;
                logEvent(APP_V2_USER_EVENTS.PANDADOC_SDK_DOCUMENT_LOADED, {
                  requirementId,
                  signType: "shiftBookingFlow",
                  timeToLoadInMilliseconds,
                });
                void updateESignWorkflowSession({
                  requirementId,
                  sessionId: signableESignWorkflowSession.id,
                  attributesToUpdate: { status: ESignWorkflowSessionStatus.START },
                });
              }}
              onError={() => {
                logEvent(APP_V2_USER_EVENTS.SHIFT_BOOKING_FLOW_DOCUMENT_SUBMISSION_FAILED, {
                  shiftId: shiftId ?? "",
                  requirementId,
                  requirementWorkflowType: RequirementWorkflowType.SIGN_WITH_HELLOSIGN,
                });
                logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
                  error: new Error("PandaDoc Embedded Signing SDK Error"),
                  metadata: { requirementId, shiftId },
                });
                errorModalState.openModal();
              }}
              onComplete={onPandaDocCompleted}
            />
          )}
        </Box>

        <PandaDocEmbeddedSessionLoadingBottomSheet modalState={loadingPandaDocModalState} />

        <DocumentSubmissionInProgressBottomSheet modalState={submittingDocumentModalState} />
      </FullScreenDialog>

      <DocumentSubmissionErrorBottomSheet
        modalState={errorModalState}
        onClick={() => {
          errorModalState.closeModal();
          openLoadingModal();
          loadSignableESignWorkflowSession({});
        }}
      />
    </>
  );
}
/* eslint-enable unicorn/prevent-abbreviations */
